/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Card, CardActions, CardContent, Container, Grid, InputAdornment, InputLabel, TextField, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { AlternateEmail } from '@material-ui/icons';
import { Link, useLocation } from 'react-router-dom';
import { getErrorMessage } from 'src/utils/getAxiosErrorMessage';
import { useApiError, useAppData, useLoading } from '../../providers';
import { useStyles } from './styles';
import { defaultLocale, extractLocaleFromPath, languages, PATH_LOGIN_AS_REPRESENTATIVE, supportedLocales } from '../../utils';
import { EmailIcon, Footer, UnauthorizedHeader } from '../../components';
import { getTranslationsByBrokerId } from '../Login/LoginService';
import { trackAmplitudeData } from '../../services/amplitudeService';
import { RestorePasswordForm } from './RestorePasswordModel';
import { restorePassword } from './RestorePasswordService';

export const RestorePassword: FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useApiError();
  const { isLoading, setIsLoading } = useLoading();
  const { logoUrl, footerLogoUrl, saveTranslations, saveLocale, locale, translations } = useAppData();
  const [logo, setLogo] = useState<string>('');
  const [footerLogo, setFooterLogo] = useState<string>('');
  const [translation, setTranslation] = useState<any>(translations);
  const [selectedLocale, setSelectedLocale] = useState<any>(languages[0]);
  const [restored, setRestored] = useState<boolean>(false);
  const location = useLocation();
  const lang = extractLocaleFromPath(location.pathname);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<RestorePasswordForm>();
  const { ref: emailHookRef, ...emailHookRefProps } = register('email', {
    required: true,
    validate: {
      validEmail: (value) => {
        const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
        return regex.test(value.replace(/\s/g, ''));
      },
    },
  });

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    if (locale) {
      const selectedLanguage = languages.find((item) => item.value === locale) || languages[0];
      setSelectedLocale(selectedLanguage);
    }
  }, [locale]);

  const getTranslationList = useCallback(
    async (newLocale: string) => {
      try {
        setIsLoading(true);
        const brokerID = process.env.REACT_APP_BROKER_ID;
        const translations = await getTranslationsByBrokerId(brokerID);
        saveTranslations(JSON.stringify(translations.data));
        setTranslation(translations.data);
        saveLocale(newLocale);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [saveTranslations, saveLocale, addError, setIsLoading],
  );

  const handleLocaleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      const selectedLanguage = languages.find((item) => item.value === (event.target.value as string)) || languages[0];
      setSelectedLocale(selectedLanguage);
      if (selectedLanguage.value !== languages[0].value) {
        trackAmplitudeData('partnerPortal_action_languageChanged', {
          page_language: selectedLanguage.value,
        });
      }
      saveLocale(selectedLanguage.value);
      await getTranslationList(selectedLanguage.value);
    },
    [getTranslationList, saveLocale],
  );

  useEffect(() => {
    (async () => {
      if (lang && supportedLocales.includes(lang)) {
        const selectedLanguage = languages.find((item) => item.value === lang) || languages[0];
        setSelectedLocale(selectedLanguage);
        if (selectedLanguage.value !== languages[0].value) {
          trackAmplitudeData('partnerPortal_action_languageChanged', {
            page_language: selectedLanguage.value,
          });
        }
        saveLocale(selectedLanguage.value);
        await getTranslationList(lang);
      } else {
        await getTranslationList(defaultLocale);
      }
    })();
  }, []);

  useEffect(() => {
    setLogo(logoUrl);
    setFooterLogo(footerLogoUrl);
  }, [logoUrl, footerLogoUrl]);

  const resetPassword = useCallback(
    async (data: RestorePasswordForm) => {
      setIsLoading(true);
  
      try {
        await restorePassword({
          userName: data.email,
          language: locale || 'en',
        });
  
        setRestored(true);
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error), {
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, locale, enqueueSnackbar],
  );

  return (
    <Grid container direction="column" className={classes.root}>
      <Grid item>
        <UnauthorizedHeader logo={logo} selectedLocale={selectedLocale} handleLocaleChange={handleLocaleChange} />
      </Grid>
      <Grid item className={classes.margin}>
        {restored ? (
          <Container className={classes.successContainer}>
            <Card className={classes.successCard}>
              <CardContent>
                <EmailIcon />
                <Typography className={classes.title}>{translation.check_mail}</Typography>
                <p>{translation.send_reset_password_link}</p>
              </CardContent>
              <CardActions className={classes.successAction}>
                <Button variant="contained" color="primary" className={classes.submitButton} component={Link} to={PATH_LOGIN_AS_REPRESENTATIVE}>
                  {translation.go_to_login_page}
                </Button>
              </CardActions>
            </Card>
          </Container>
        ) : (
          <Container className={classes.container}>
            <form onSubmit={handleSubmit(resetPassword)} noValidate>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.title}>{translation.restore_password}</Typography>
                  <Typography className={classes.specifyEmail}>{translation.specify_email}</Typography>
                  <div className={classes.padding}>
                    <InputLabel className={classes.label}>
                      {translation.text_1371}
                      <span className={classes.required}>*</span>
                    </InputLabel>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="email"
                      type="email"
                      margin="dense"
                      variant="outlined"
                      placeholder="yourname@gmail.com"
                      className={classes.input}
                      error={!!errors.email}
                      helperText={errors?.email?.type === 'required' ? translation.text_1358 : errors?.email?.type === 'validEmail' ? translation.email_must_be_a_valid_email : errors?.email?.message}
                      inputRef={emailHookRef}
                      {...emailHookRefProps}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <AlternateEmail className={classes.icon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button type="submit" variant="contained" color="primary" className={classes.submitButton} fullWidth disabled={isLoading}>
                    {translation.text_1375}
                  </Button>
                  <Button variant="outlined" fullWidth className={classes.cancelButton} component={Link} to={PATH_LOGIN_AS_REPRESENTATIVE}>
                    {translation.text_28}
                  </Button>
                </CardActions>
              </Card>
            </form>
          </Container>
        )}
      </Grid>
      <Grid item>
        <Footer translation={translation} logo={footerLogo} />
      </Grid>
    </Grid>
  );
};
